import { useEffect, useRef } from 'react';
import { createCamera2D } from '../../engine/hex/camera2d';
import { createMapModel, type MapModel } from '../../engine/map/mapModel';
import { generateIslandTiles } from '../../engine/map/islandGenerator';
import { createStarterSettlement } from '../../engine/settlement';
import { tickSettlementProduction } from '../../engine/settlement/manager/productionManager';
import { spawnEncounters } from '../../engine/battle/encounters';
import {
    loadGameConfig,
    mergeGameConfig,
    setGameConfig,
    type GameConfig,
} from '../../config/gameConfig';
import { drawFrame, type HexMapState } from '../../engine/render/renderer';
import { bindPointerController } from '../../engine/input/pointerController';
import { bindWheelController } from '../../engine/input/wheelController';
import { clampRectToViewport, getModalSize } from '../../engine/render/modalLayout';
import { runWildlifeMigration } from '../../tick/world';
import { runFoundingExpeditions } from '../../tick/foundingExpedition';
import { runTransportTick } from '../../tick/transport';
import { runScoutingMissions } from '../../tick/scouting';
import { runSettlementAlerts } from '../../tick/alerts';
import { getSettlementTierData } from '../../engine/economy/settlementTiers';
import { IconRegistry } from '../overlay/assets/IconRegistry';
import { registerResourceIcons } from '../overlay/assets/resourceIcons';
import { SoundManager } from '../../audio/SoundManager';
import {
    applyTutorialScenario,
    startTutorial,
    updateTutorialProgress,
} from '../../tutorials/runtime';

type HexMapControllerOptions = {
    configOverride?: Partial<GameConfig>;
    tutorialId?: string | null;
};

export function useHexMapController(options: HexMapControllerOptions = {}) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const stateRef = useRef<HexMapState | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext('2d')!;
        canvas.style.touchAction = 'none';

        const viewport = { width: window.innerWidth, height: window.innerHeight };
        const camera = createCamera2D(() => viewport);
        const baseConfig = loadGameConfig();
        const config = options.configOverride
            ? mergeGameConfig(baseConfig, options.configOverride)
            : baseConfig;
        setGameConfig(config);
        const model = createMapModel(generateIslandTiles(config));
        const landTiles = model.tiles.filter((tile) => tile.biome.type !== 'water');
        const cityTile = pickStartingTile(model, landTiles, config.map.size);
        ensureNearbyIron(model, cityTile);
        const tierData = getSettlementTierData(config.initial.settlement.tier);
        const regionTiles = model.getTilesInRadius(
            cityTile.position.q,
            cityTile.position.r,
            cityTile.position.s,
            tierData.radius,
        );
        const settlement = createStarterSettlement(cityTile, regionTiles);
        const settlementsById = { [settlement.id]: settlement };
        const settlementByTileId = { [settlement.tileId]: settlement };
        const ownedTilesSource = regionTiles.length > 0 ? regionTiles : [cityTile];
        const ownedTileIds = new Set(ownedTilesSource.map((tile) => tile.id));
        const discoveredTileIds = new Set(ownedTileIds);
        const enemyForces = spawnEncounters({
            tiles: model.tiles,
            types: config.battle.enemies,
            chance: options.tutorialId ? config.battle.encounterChance : 1,
            seed: Number.isFinite(config.map.seed) ? config.map.seed! + 11 : 1348,
            origin: options.tutorialId ? undefined : cityTile.position,
            scaling: options.tutorialId ? null : config.battle.encounterScaling,
        });
        ownedTileIds.forEach((tileId) => {
            delete enemyForces[tileId];
        });

        const overlayIcons = new IconRegistry({
            onReady: () => {
                if (stateRef.current) stateRef.current.dirty = true;
            },
        });
        overlayIcons.register('settings', '/images/icon/settings.png');
        registerResourceIcons(overlayIcons);

        const isMobileViewport = viewport.width <= 720;
        const soundManager = new SoundManager();
        const state: HexMapState = {
            pointer: { x: 0, y: 0 },
            dragging: false,
            dragStart: { x: 0, y: 0 },
            camStart: { x: 0, y: 0 },
            hover: null,
            ownedTileIds,
            discoveredTileIds,
            settlementsById,
            settlementByTileId,
            foundingExpeditions: [],
            expeditionIdCounter: 0,
            transportRoutes: [],
            transportIdCounter: 0,
            scoutingMissions: [],
            scoutingIdCounter: 0,
            battleReports: [],
            battleReportIdCounter: 0,
            scoutPlanningMissionId: null,
            enemyForces,
            dismissedNotifications: new Set(),
            alerts: [],
            alertIdCounter: 0,
            overlay: {
                showNotifications: !isMobileViewport,
                speedMenuOpen: false,
                languageMenuOpen: false,
            },
            config,
            settingsEditor: {
                text: JSON.stringify(config, null, 2),
                error: null,
            },
            modals: [],
            activeModalId: null,
            modalDragId: null,
            modalDragOffset: { x: 0, y: 0 },
            isModalHover: false,
            actionBarActions: [],
            overlayAssets: { icons: overlayIcons },
            audio: { manager: soundManager },
            tutorial: null,
            simulation: {
                running: true,
                speed: 1,
                accumulator: 0,
                lastTime: 0,
                tick: 0,
            },
            dirty: true,
        };
        stateRef.current = state;

        const SIM_STEP_MS = config.time.tickSeconds * 1000;
        if (options.tutorialId) {
            applyTutorialScenario(
                options.tutorialId,
                state,
                settlement.id,
                regionTiles,
                model.tiles,
            );
            startTutorial(state, camera, viewport, options.tutorialId, (id) => {
                return model.tiles.find((tile) => tile.id === id) || null;
            });
        }

        const advanceSimulation = (deltaMs: number) => {
            if (!state.simulation.running) return false;
            state.simulation.accumulator += deltaMs * state.simulation.speed;
            let changed = false;
            while (state.simulation.accumulator >= SIM_STEP_MS) {
                state.simulation.accumulator -= SIM_STEP_MS;
                state.simulation.tick += 1;
                for (const settlement of Object.values(state.settlementsById)) {
                    if (tickSettlementProduction(settlement, state.simulation.tick)) {
                        changed = true;
                    }
                }
                if (runFoundingExpeditions(model, state)) changed = true;
                if (runTransportTick(state)) changed = true;
                if (runScoutingMissions(model, state)) changed = true;
                if (runSettlementAlerts(state)) changed = true;
                if (runWildlifeMigration(model, state)) changed = true;
            }
            return changed;
        };

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            viewport.width = window.innerWidth;
            viewport.height = window.innerHeight;
            canvas.width = Math.floor(viewport.width * dpr);
            canvas.height = Math.floor(viewport.height * dpr);
            canvas.style.width = `${viewport.width}px`;
            canvas.style.height = `${viewport.height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const size = getModalSize(viewport);
            state.modals.forEach((modal) => {
                modal.rect = clampRectToViewport({ ...modal.rect, w: size.w, h: size.h }, viewport);
            });
            state.dirty = true;
        };

        const markDirty = () => {
            state.dirty = true;
        };

        const stopPointer = bindPointerController(
            canvas,
            camera,
            model,
            state,
            () => viewport,
            markDirty,
        );
        const stopWheel = bindWheelController(canvas, camera, state, markDirty);

        let animationId = 0;
        const tick = (time: number) => {
            if (state.simulation.lastTime === 0) {
                state.simulation.lastTime = time;
            }
            const delta = time - state.simulation.lastTime;
            state.simulation.lastTime = time;
            if (delta > 0 && advanceSimulation(delta)) {
                state.dirty = true;
            }
            updateTutorialProgress(state);
            state.audio.manager.sync(state);
            if (state.dirty) {
                drawFrame(ctx, model, camera, state, viewport);
                state.dirty = false;
            }
            animationId = window.requestAnimationFrame(tick);
        };

        resize();
        window.addEventListener('resize', resize);
        animationId = window.requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('resize', resize);
            stopPointer();
            stopWheel();
            window.cancelAnimationFrame(animationId);
        };
    }, [options.configOverride, options.tutorialId]);

    return { canvasRef, stateRef };
}

function ensureNearbyIron(
    model: MapModel,
    tile: { position: { q: number; r: number; s: number } },
): void {
    const radius = 2;
    const candidates: typeof model.tiles = [];
    for (let dq = -radius; dq <= radius; dq += 1) {
        for (
            let dr = Math.max(-radius, -dq - radius);
            dr <= Math.min(radius, -dq + radius);
            dr += 1
        ) {
            const nq = tile.position.q + dq;
            const nr = tile.position.r + dr;
            const ns = -nq - nr;
            const neighbor = model.getTileByCube(nq, nr, ns);
            if (!neighbor || neighbor.biome.type === 'water') continue;
            if (neighbor.deposits?.ironDeposit) return;
            candidates.push(neighbor);
        }
    }
    if (candidates.length === 0) return;
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    pick.deposits = { ...(pick.deposits || {}), ironDeposit: Math.floor(80 + Math.random() * 100) };
}

function hasFishWithinRadius(
    model: MapModel,
    tile: { position: { q: number; r: number; s: number } },
    radius: number,
): boolean {
    for (let dq = -radius; dq <= radius; dq += 1) {
        for (
            let dr = Math.max(-radius, -dq - radius);
            dr <= Math.min(radius, -dq + radius);
            dr += 1
        ) {
            const nq = tile.position.q + dq;
            const nr = tile.position.r + dr;
            const ns = -nq - nr;
            const neighbor = model.getTileByCube(nq, nr, ns);
            if (!neighbor) continue;
            if (neighbor.ecology?.fishCap) return true;
            if (neighbor.resources.fish && (neighbor.resources.fish as { amount?: number }).amount)
                return true;
        }
    }
    return false;
}

function pickStartingTile(
    model: MapModel,
    landTiles: typeof model.tiles,
    size: 'small' | 'medium' | 'huge',
) {
    if (landTiles.length === 0) return model.tiles[0];
    if (size !== 'small') {
        return landTiles[Math.floor(Math.random() * landTiles.length)];
    }
    const fishFriendly = landTiles.filter((tile) => hasFishWithinRadius(model, tile, 3));
    if (fishFriendly.length > 0) {
        return fishFriendly[Math.floor(Math.random() * fishFriendly.length)];
    }
    return landTiles[Math.floor(Math.random() * landTiles.length)];
}
